/**
 * The app's theme: «Обычная», «Облака», or a photo of your own behind the
 * ordinary palette. One choice for the whole app rather than one per
 * conversation — the handoff is explicit that a theme reaches the chat list,
 * the menus and the settings, not only the wallpaper it is recognised by.
 *
 * Almost nothing here paints anything. A theme is a `data-theme` attribute on
 * <html> and a block of overrides in tokens.css, so choosing one re-colours
 * every rule in the app that already reads a token, and no component has to
 * learn that themes exist. The two exceptions are the ones a stylesheet cannot
 * hold: a picked photo (a data URL) and the browser's own chrome colour.
 *
 * Like the smiley ranking, the choice lives in localStorage: it is a preference
 * rather than content, and a theme picked on a phone has no business following
 * the account onto someone else's screen. Moving it to the profile later means
 * changing `load` and `persist` and nothing else.
 */
import { useSyncExternalStore } from 'react';

export type ThemeId = 'default' | 'clouds' | 'gothic' | 'custom';

export interface Theme {
  id: ThemeId;
  /** The picked photo as a data URL. Only ever set when `id` is `custom`. */
  photo: string | null;
}

/** The themes proper, in the order the picker's rail shows them. */
export const THEMES: { id: Exclude<ThemeId, 'custom'>; name: string }[] = [
  { id: 'default', name: 'Обычная' },
  { id: 'clouds', name: 'Облака' },
  { id: 'gothic', name: 'Готика' },
];

/** What «Своё фото» is called wherever a theme has to name itself. */
const CUSTOM_NAME = 'Своё фото';

/**
 * The colour the browser tints its own chrome with — the status bar of an
 * installed app on both phones, and Safari's toolbar on iOS.
 *
 * Each theme's `--surface`, mirrored here because CSS cannot be read back
 * before it is parsed. Not the accent, which is what this used to be: the app
 * paints `--surface` right up to the status bar (`.app-frame` pays the top
 * inset as padding), so anything else draws a band of a second colour across
 * the top of the screen. Inside the native shells nothing reads this at all —
 * both draw the page edge to edge, so the page itself is the status bar's
 * background and the theme reaches it without being told to.
 */
const CHROME: Record<ThemeId, string> = {
  default: '#ffffff',
  clouds: '#f7f4fe',
  // «Готика» draws its panels as glass, but the strip under the status bar is
  // the frame's own surface rather than the wallpaper, so this is the colour
  // that glass collapses to and not one of its translucent values — a status
  // bar cannot be see-through onto something that is not behind it.
  gothic: '#fcfcfc',
  custom: '#ffffff',
};

const KEY = 'theme.v1';
/** What the choice was called while it was only a wallpaper. See `migrate`. */
const OLD_KEY = 'wallpaper.v1';

const DEFAULT: Theme = { id: 'default', photo: null };

/**
 * The CSS `background` behind a thread or a preview — one answer for both, so
 * a preview can never disagree with the conversation it is promising.
 *
 * Every theme but «Своё фото» already carries its wallpaper as a token, and
 * reading it back through var() means the rail's cards can each scope
 * themselves with `data-theme` and get their own. A photo cannot live in a
 * stylesheet, so it is the one that arrives as a value.
 *
 * `custom` without a photo falls back to the token as well: the stored photo
 * can go missing (a cleared store, a failed save) and a transparent thread is
 * worse than no wallpaper.
 */
export function themeBackground(theme: Theme): string {
  if (theme.id === 'custom' && theme.photo) {
    return `center / cover no-repeat url("${theme.photo}")`;
  }
  return 'var(--wallpaper)';
}

/** How the settings row and the picker's caption name the current choice. */
export function themeName(theme: Theme): string {
  if (theme.id === 'custom') return CUSTOM_NAME;
  return THEMES.find((known) => known.id === theme.id)?.name ?? '';
}

function parse(raw: string | null): Theme {
  if (raw === null) return DEFAULT;
  try {
    const stored = JSON.parse(raw) as Partial<Theme>;
    const known = stored.id === 'custom' || THEMES.some((theme) => theme.id === stored.id);
    const id = known ? (stored.id as ThemeId) : 'default';
    const photo = typeof stored.photo === 'string' ? stored.photo : null;
    return { id, photo: id === 'custom' ? photo : null };
  } catch {
    // Private mode, a disabled store, or something that is not JSON any more.
    return DEFAULT;
  }
}

/**
 * The wallpaper preference this replaced. Its two tinted presets are gone with
 * the redesign and land on «Обычная», but a photo somebody picked is their own
 * file — losing it silently on an update is the one thing worth the migration.
 */
function migrate(): Theme | null {
  const raw = localStorage.getItem(OLD_KEY);
  if (raw === null) return null;
  const old = parse(raw);
  localStorage.removeItem(OLD_KEY);
  return old;
}

function load(): Theme {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw !== null) return parse(raw);
    const migrated = migrate();
    if (migrated) persist(migrated);
    return migrated ?? DEFAULT;
  } catch {
    return DEFAULT;
  }
}

function persist(theme: Theme): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(theme));
    return true;
  } catch {
    // Almost always the quota, and almost always a photo: the choice still
    // applies for this session, it just will not survive a reload.
    return false;
  }
}

/**
 * The half of a theme that is not a token: the attribute the overrides hang
 * off, and the colour the browser paints its own bar with — on Android that
 * bar is above the app's own header, so a theme that skipped it would be
 * wearing last theme's hat.
 */
function apply(theme: Theme): void {
  // Guarded because the logic above is tested without a DOM — see the two test
  // projects in client/vite.config.ts.
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme.id;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', CHROME[theme.id]);
}

let current = load();
const listeners = new Set<() => void>();
apply(current);

/** Stable between changes, which is what useSyncExternalStore requires. */
export function theme(): Theme {
  return current;
}

/**
 * Applies a choice everywhere at once. `false` means it could not be saved —
 * the caller says so rather than pretending the pick did not happen.
 */
export function setTheme(id: ThemeId, photo: string | null = null): boolean {
  // Choosing a theme drops the photo, which is why «Своё фото» always opens the
  // picker: keeping a picture nobody asked to come back to is how a preference
  // starts holding several megabytes of something long forgotten.
  const kept = id === 'custom' ? (photo ?? current.photo) : null;
  current = { id, photo: kept };
  const saved = persist(current);
  apply(current);
  for (const listener of listeners) listener();
  return saved;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The theme, re-rendering whoever reads it the moment it changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, theme, theme);
}

/**
 * The longest edge a stored photo keeps. Big enough to cover a phone screen
 * without visible softness, small enough that the JPEG behind it lands well
 * inside the few megabytes localStorage will hold — a wallpaper straight off a
 * modern camera would not fit at all.
 */
const MAX_EDGE = 1440;
const QUALITY = 0.82;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Не удалось прочитать фото'));
    image.src = src;
  });
}

/**
 * A picked file turned into the data URL the wallpaper is stored as: scaled to
 * fit `MAX_EDGE` and re-encoded as JPEG. Transparency is not worth keeping here
 * — the photo covers the whole thread, so there is nothing behind it to show.
 */
export async function photoFromFile(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Не удалось обработать фото');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', QUALITY);
  } finally {
    URL.revokeObjectURL(url);
  }
}
